// The one place a fact value's tone is derived from its field definition
// (#12021). Every surface that paints an enum value — the table's chips, the
// performance grid, the record panels, the editor chips — calls this instead
// of assembling `enumValueToneStyles` options by hand, so the vocabulary
// check, the field hue and the status precedence are the same everywhere.
// Before this, each surface built the options itself and they disagreed: the
// editor skipped the vocabulary check, the performance grid skipped the field
// hue, and the table's chip layer and its text helper each ran the check —
// which is how a value outside the option list ended up as a pill inside a
// pill.

import { enumValueToneStyles, type EnumToneStyles } from "./enumTone";
import { isInteractionTypeFact, type Provenance } from "../Dashboard/types";

/**
 * The fields of a definition the tone reads. Structural, so both the grid's
 * `SchemaFact` and the record page's `FactDefinition` qualify.
 */
export interface ToneFieldSource {
  name?: string | null;
  is_hierarchical?: boolean | null;
  scalar_values?: unknown[] | null;
  enum_colors?: Record<string, string> | null;
  enum_labels?: Record<string, string> | null;
  status_states?: Record<string, string> | null;
  category_names?: Record<string, string> | null;
  provenance?: Provenance | null;
}

// The vocabulary distinguishes its VALUES by hue: the org configured
// per-value colours or tag categories, or the field is the first-class
// interaction type (a tag taxonomy by construction). Everything else is a
// plain enum whose hue derives from the field (design round 8).
export function isValueHuedVocabulary(schema: {
  enum_colors?: Record<string, string> | null;
  category_names?: Record<string, string> | null;
  provenance?: Provenance | null;
}): boolean {
  return (
    Boolean(schema.enum_colors && Object.keys(schema.enum_colors).length > 0) ||
    Boolean(
      schema.category_names && Object.keys(schema.category_names).length > 0
    ) ||
    isInteractionTypeFact(schema)
  );
}

/**
 * Whether `key` falls OUTSIDE the field's declared vocabulary — a
 * classifier's "other: …" invention, or a connector value the dropdown's
 * hand-typed option list never named. Such a key takes the none tone: a
 * hashed hue would claim a category nobody defined, where grey states the
 * classification gap honestly (#11426, keeper ruling on the Documents type
 * column).
 *
 * A HIERARCHICAL value is exempt: its key is the path's leaf while
 * `scalar_values` holds the paths, so a flat membership test would condemn
 * every value the org's own tree produced. An empty or absent option list
 * judges nothing (a synthesized schema declares its vocabulary through the
 * tone maps instead), and a key `enum_labels` names is listed by definition.
 *
 * No `is_enum` gate: the `chkrmsa_scalar_values` check constraint on
 * `rms_attributes` makes `is_enum` true exactly when `scalar_values` is
 * non-null, so a populated option list IS the enum declaration.
 */
export function isUnlistedEnumKey(
  schema: ToneFieldSource,
  key: string
): boolean {
  if (schema.is_hierarchical) return false;
  const values = schema.scalar_values;
  if (!Array.isArray(values) || values.length === 0) return false;
  if (values.map(String).includes(key)) return false;
  if (schema.enum_labels && key in schema.enum_labels) return false;
  return true;
}

/** The tone for one value of a field, by the one rule every surface shares. */
export function factValueTone(
  schema: ToneFieldSource,
  key: string
): EnumToneStyles {
  return enumValueToneStyles(key, {
    statusStates: schema.status_states,
    inVocabulary: !isUnlistedEnumKey(schema, key),
    categoryKey: schema.category_names?.[key],
    // A plain enum's hue derives from the FIELD (design round 8); only a
    // value-hued taxonomy (org-coloured vocabularies, the interaction-type
    // field) keeps per-value hues.
    fieldKey: schema.name,
    valueHued: isValueHuedVocabulary(schema),
    // Configured colors are hue requests (CD org-color ruling): the Settings
    // pick drives the hue, snapped onto the ramp — the raw hex never paints.
    enumColors: schema.enum_colors,
  });
}
